import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter } from 'rxjs';
import { AuthService, Module, Navigation } from '@services/auth.service';
import { NavigazioneService } from '@services/navigazione.service';

/**
 * Menu laterale.
 *
 * Riscritto: prima il link del modulo era in `position: absolute` sopra un
 * `padding-top: 48px`, la freccia del gruppo era il carattere `\25BA` in un
 * `::after` e i colori pescavano da token Material scelti a caso
 * (`--mat-select-panel-background-color`, `--mdc-fab-container-color`).
 */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './sidebar.component.html',
})
export class SidebarComponent {
  readonly authService = inject(AuthService);
  readonly navigazione = inject(NavigazioneService);
  private readonly router = inject(Router);

  readonly urlCorrente = signal(this.router.url);

  /**
   * Gruppi aperti o chiusi a mano. Se un gruppo non compare qui vale la
   * regola implicita: e' aperto quando contiene la pagina che stai guardando.
   */
  private readonly apertiAMano = signal<Record<string, boolean>>({});

  constructor() {
    this.router.events
      .pipe(
        filter((evento) => evento instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.urlCorrente.set(this.router.url);
        this.navigazione.haNavigato();
      });
  }

  eDashboard(): boolean {
    return this.urlCorrente() === '/';
  }

  eGruppoAperto(modulo: Module): boolean {
    const aMano = this.apertiAMano()[modulo.id];
    return aMano ?? this.contienePaginaCorrente(modulo);
  }

  alternaGruppo(modulo: Module): void {
    const aperto = this.eGruppoAperto(modulo);
    this.apertiAMano.update((stato) => ({ ...stato, [modulo.id]: !aperto }));
  }

  contienePaginaCorrente(modulo: Module): boolean {
    return this.urlCorrente().split('/').filter(Boolean)[0] === modulo.id;
  }

  ePaginaCorrente(navigazione: Navigation): boolean {
    return this.urlCorrente() === navigazione.url;
  }

  vaiA(url: string): void {
    this.router.navigateByUrl(url);
  }
}
